import { BlockComponentProps } from 'client-react-block-renderer';
import React, { useCallback, useEffect, useState } from 'react';
import t from 'rcm-i18n';
import axios from 'axios';
import './PwsCouponOwnerControlsComponent.scss'
import CouponRow from './CouponRow';
import { CouponFromConfigConfig } from './types';
import { cloneDeep } from 'lodash';
import { PWS_COUPONS_URLS } from '@reliv/pws-core';
import LoadingSpinner from 'loading-spinner/LoadingSpinner';

type Props = BlockComponentProps<Data, Config>;

interface Config {
}

interface Data {
}

function mergeCouponCodesWithCustomCouponCodes(
    couponCodes: CouponFromConfigConfig[],
    customCouponCodes: Record<string, string>
) {
    const mergedCouponCodes = cloneDeep(couponCodes);
    mergedCouponCodes.forEach(couponCodeObject => {
        couponCodeObject.couponCode = couponCodeObject.defaultCouponCode;
    })
    for (const [key, value] of Object.entries(customCouponCodes)) {
        const couponCodeObject = mergedCouponCodes.find(potentialCouponCodeObject => potentialCouponCodeObject.id === key);
        if (couponCodeObject) {
            couponCodeObject.couponCode = value;
        }
    }
    return mergedCouponCodes;
}

export default function PwsCouponOwnerControlsComponent(props: Props) {
    const [pwsCouponPublicConfig, setPwsCouponPublicConfig] = useState(null);
    const [enabledCouponCodeIds, setEnabledCouponCodeIds] = useState([]);
    const [formState, setFormState] = useState('loading');
    const [customCouponCodes, setCustomCouponCodes] = useState({});

    const handleSaveClick = useCallback((enabledCouponCodeIds, customCouponCodes) => {
        setFormState('saving');
        axios.put(
            PWS_COUPONS_URLS.pwsOwnerSettings,
            {
                enabledCouponCodeIds: enabledCouponCodeIds,
                customCouponCodes: customCouponCodes
            }
        ).then(response => {
            const pwsOwnerCouponSettings = response.data;
            setEnabledCouponCodeIds(pwsOwnerCouponSettings.enabledCouponCodeIds);
            setCustomCouponCodes(pwsOwnerCouponSettings.customCouponCodes);
            setFormState('saved');
        }).catch(() => {
            setFormState('error');
        });
    }, [enabledCouponCodeIds, customCouponCodes]);

    const handleCouponEnabledToggle = useCallback((couponId: string) => {
        let newEnabledCouponCodeIds
        if (enabledCouponCodeIds.includes(couponId)) {
            newEnabledCouponCodeIds = enabledCouponCodeIds.filter(thisCouponId => thisCouponId !== couponId);
        } else {
            newEnabledCouponCodeIds = [...enabledCouponCodeIds, couponId];
        }
        handleSaveClick(newEnabledCouponCodeIds, customCouponCodes);
    }, [enabledCouponCodeIds, setEnabledCouponCodeIds, handleSaveClick]);

    useEffect(() => {
        axios.get(PWS_COUPONS_URLS.publicGlobalConfig).then(response => {
            setPwsCouponPublicConfig(response.data);
            axios.get(PWS_COUPONS_URLS.pwsOwnerSettings).then(response => {
                const pwsOwnerCouponSettings = response.data;
                setEnabledCouponCodeIds(pwsOwnerCouponSettings.enabledCouponCodeIds);
                setCustomCouponCodes(pwsOwnerCouponSettings.customCouponCodes);
                setFormState('ready');
            }).catch(() => {
                setFormState('error');
            });
        }).catch(() => {
            setFormState('error');
        });
    }, [])

    const handleCouponCodeValueChange = useCallback((couponId: string, newCouponCodeValue: string) => {
        const newCustomCouponCodes = { ...customCouponCodes, [couponId]: newCouponCodeValue };
        handleSaveClick(enabledCouponCodeIds, newCustomCouponCodes);
    }, [customCouponCodes, handleSaveClick])

    let mergedCouponCodes;
    if (pwsCouponPublicConfig?.couponCodes && customCouponCodes) {
        mergedCouponCodes = mergeCouponCodesWithCustomCouponCodes(pwsCouponPublicConfig.couponCodes, customCouponCodes);
    }

    return <div className="PwsCouponOwnerControlsComponent">
        {formState === 'loading' &&
            <LoadingSpinner />
        }
        {formState === 'error' &&
            <div className="alert alert-warning">{t('An error occurred')}</div>
        }
        <div>
        </div>
        {!['loading'].includes(formState) && mergedCouponCodes?.map(coupon =>
            <CouponRow key={coupon.id}
                coupon={coupon}
                onCouponToggle={handleCouponEnabledToggle}
                enabledCouponCodeIds={enabledCouponCodeIds}
                onCouponCodeValueChange={handleCouponCodeValueChange}
            />
        )}
    </div>
}